'use client';

/**
 * Navigation **Block** shell.
 *
 * Identity: `Navigation-{Arrangement}-{Chrome}` (e.g. Navigation-Rail-Overlay).
 * - Arrangement → `registryBlockKey` / `data-nav-arrangement`
 *   (Rail · Compass · Curtain · Ledger · Monogram)
 * - Chrome → `content.navPresentation` / `data-nav-presentation` (classic, overlay, …)
 * - Outfit → site theme tokens (not this file)
 *
 * Do not split into Chrome-suffixed TSX files; remix axes via data, not filenames.
 */
import { EditableContent } from '../../edit/EditableContent';
import { EditableMedia } from '../../edit/EditableMedia';
import { pickBlockField } from '../../lib/localized';
import { pickMediaUrl } from '../../lib/media';
import {
  readBlockCtaIntent,
  resolveBlockCtaHref,
  resolveSectionHref,
} from '../../lib/resolveSectionHref';
import { activateNavDrawerHashLink, closeNavDrawerFrom } from '../../lib/navDrawerHash';
import { pickChrome } from '../../lib/siteChromeCopy';
import type { BlockRenderProps } from '../../types';
import { SiteSocialLinks } from '../shared/SiteSocialLinks';
import {
  NAV_PRESENTATION,
  NAV_PRESENTATION_FROM_REGISTRY,
  pickSiteSocialLinksFromContent,
  resolveNavigationTree,
  readNavPresentationBlockOverride,
  resolveNavArrangementFromRegistry,
  resolveNavCurrentPageSlug,
  navPresentationUsesCinematicLogo,
  resolveNavLogoSurface,
  resolveNavPresentation,
  resolveOverlayAdaptivePanelLogoPath,
  navNodeIsCurrentPage,
  retargetPlatformNavLogoPathForSurface,
  isNavPresentation,
  buildLocaleSwitcherHref,
  getPlatformLocaleDefinition,
  type ResolvedNavigationNode,
} from '@web-studio/shared-schemas';

function NavLinkItem({
  item,
  blockId,
  editable,
  target,
  onNavigateToPageSlug,
  currentPageSlug,
  depth = 0,
  locale,
  forceMega = false,
}: {
  item: ResolvedNavigationNode;
  blockId: string;
  editable?: boolean;
  target: BlockRenderProps['activeEditTarget'];
  onNavigateToPageSlug?: (slug: string) => void;
  currentPageSlug: string | null;
  depth?: number;
  locale: string;
  forceMega?: boolean;
}) {
  const hasChildren = item.children.length > 0;
  const isMega = hasChildren && (forceMega || item.layout === 'mega');
  const subToggleId = `ws-nav-sub-${blockId}-${item.id}`;
  const current = navNodeIsCurrentPage(item, currentPageSlug);

  const allowNavigation = Boolean(onNavigateToPageSlug) && !editable;
  const linkProps = {
    as: 'a' as const,
    fieldKey: item.fieldKey,
    editable,
    activeEditTarget: target,
    className: 'ws-nav__link',
    href: item.pageSlug && allowNavigation ? '#' : item.href,
    'aria-current': current ? ('page' as const) : undefined,
    onClick:
      item.pageSlug && allowNavigation
        ? (event: { preventDefault: () => void }) => {
            event.preventDefault();
            onNavigateToPageSlug!(item.pageSlug!);
          }
        : undefined,
  };

  if (!hasChildren) {
    return (
      <li className="ws-nav__item" data-nav-depth={depth}>
        <EditableContent {...linkProps}>{item.label}</EditableContent>
      </li>
    );
  }

  return (
    <li
      className={[
        'ws-nav__item',
        'ws-nav__item--has-children',
        isMega ? 'ws-nav__item--mega' : 'ws-nav__item--dropdown',
      ]
        .filter(Boolean)
        .join(' ')}
      data-nav-depth={depth}
    >
      <input
        type="checkbox"
        id={subToggleId}
        className="ws-nav__sub-toggle"
        aria-hidden="true"
        tabIndex={-1}
      />
      <div className="ws-nav__item-row">
        <EditableContent {...linkProps}>{item.label}</EditableContent>
        <label htmlFor={subToggleId} className="ws-nav__sub-expand" aria-label={pickChrome('navSubmenu', locale)}>
          <span className="ws-nav__sub-expand-icon" aria-hidden="true" />
        </label>
      </div>
      <ul className={['ws-nav__submenu', isMega ? 'ws-nav__submenu--mega' : ''].filter(Boolean).join(' ')}>
        {item.children.map((child) => (
          <NavLinkItem
            key={child.id}
            blockId={blockId}
            item={child}
            editable={editable}
            target={target}
            onNavigateToPageSlug={onNavigateToPageSlug}
            currentPageSlug={currentPageSlug}
            depth={depth + 1}
            locale={locale}
            forceMega={forceMega}
          />
        ))}
      </ul>
    </li>
  );
}

function NavLinks({
  blockId,
  items,
  editable,
  target,
  onNavigateToPageSlug,
  currentPageSlug,
  locale,
  forceMega = false,
}: {
  blockId: string;
  items: ResolvedNavigationNode[];
  editable?: boolean;
  target: BlockRenderProps['activeEditTarget'];
  onNavigateToPageSlug?: (slug: string) => void;
  currentPageSlug: string | null;
  locale: string;
  forceMega?: boolean;
}) {
  if (items.length === 0) {
    return null;
  }

  return (
    <nav aria-label={pickChrome('navMain', locale)}>
      <ul className="ws-nav__links">
        {items.map((item) => (
          <NavLinkItem
            key={item.id}
            blockId={blockId}
            item={item}
            editable={editable}
            target={target}
            onNavigateToPageSlug={onNavigateToPageSlug}
            currentPageSlug={currentPageSlug}
            locale={locale}
            forceMega={forceMega}
          />
        ))}
      </ul>
    </nav>
  );
}

function NavLocaleSwitcher({
  locale,
  defaultLocale,
  supportedLocales,
  editable,
  publicPathPrefix,
  pathname,
  onLocaleChange,
  slot,
}: {
  locale: string;
  defaultLocale: string;
  supportedLocales: string[];
  editable?: boolean;
  publicPathPrefix?: string | null;
  /** Current path (may include locale prefix). Falls back to `/` when omitted. */
  pathname?: string | null;
  onLocaleChange?: (locale: string) => void;
  /** `bar` = desktop Compass cluster; `drawer` = mobile + Rail inline row. */
  slot: 'bar' | 'drawer';
}) {
  if (supportedLocales.length < 2) {
    return null;
  }

  const currentPath =
    pathname ??
    (typeof window !== 'undefined' ? window.location.pathname : '/');
  const aria = pickChrome('navLocale', locale);
  const pairMode = supportedLocales.length === 2;

  const localeLink = (code: string) => {
    const active = code === locale;
    const def = getPlatformLocaleDefinition(code);
    const href = buildLocaleSwitcherHref({
      targetLocale: code,
      defaultLocale,
      supportedLocales,
      pathname: currentPath,
      publicPathPrefix,
    });
    return (
      <a
        className={['ws-nav__locale', active ? 'ws-nav__locale--active' : '']
          .filter(Boolean)
          .join(' ')}
        href={editable || onLocaleChange ? '#' : href}
        aria-current={active ? 'true' : undefined}
        lang={code}
        onClick={
          editable || onLocaleChange
            ? (event) => {
                event.preventDefault();
                if (!editable && onLocaleChange && code !== locale) {
                  onLocaleChange(code);
                }
              }
            : undefined
        }
      >
        <span className="ws-nav__locale-code">{def?.pillLabel ?? code.toUpperCase()}</span>
        {pairMode ? null : (
          <span className="ws-nav__locale-name">{def?.nativeLabel ?? code}</span>
        )}
      </a>
    );
  };

  /* Two locales: compact DE | EN toggle — no dropdown chrome. */
  if (pairMode) {
    return (
      <nav
        className={`ws-nav__locale-pair ws-nav__locales--${slot}`}
        aria-label={aria}
      >
        {supportedLocales.map((code, index) => (
          <span key={code} className="ws-nav__locale-pair-item">
            {index > 0 ? (
              <span className="ws-nav__locale-sep" aria-hidden="true">
                |
              </span>
            ) : null}
            {localeLink(code)}
          </span>
        ))}
      </nav>
    );
  }

  const activeDef = getPlatformLocaleDefinition(locale);
  const triggerLabel = activeDef?.pillLabel ?? locale.toUpperCase();

  return (
    <details className={`ws-nav__locale-switch ws-nav__locales--${slot}`}>
      <summary className="ws-nav__locale-trigger" aria-label={aria}>
        <span className="ws-nav__locale-code">{triggerLabel}</span>
        <span className="ws-nav__locale-chevron" aria-hidden="true" />
      </summary>
      <ul className="ws-nav__locale-menu" role="list">
        {supportedLocales.map((code) => (
          <li key={code} className="ws-nav__locale-item">
            {localeLink(code)}
          </li>
        ))}
      </ul>
    </details>
  );
}

function navArrangementClass(arrangement: string): string {
  switch (arrangement) {
    case 'compass':
      return 'ws-nav-compass';
    case 'curtain':
      return 'ws-nav-curtain';
    case 'ledger':
      return 'ws-nav-ledger';
    case 'monogram':
      return 'ws-nav-monogram';
    case 'signal':
      return 'ws-nav-signal';
    default:
      return 'ws-nav-rail';
  }
}

function NavCta({
  cta,
  editable,
  target,
  className,
  href,
}: {
  cta: string;
  editable?: boolean;
  target: BlockRenderProps['activeEditTarget'];
  className?: string;
  href: string;
}) {
  if (!cta) {
    return null;
  }

  return (
    <EditableContent
      as="a"
      fieldKey="ctaLabel"
      editable={editable}
      activeEditTarget={target}
      className={['ws-nav__cta', className].filter(Boolean).join(' ')}
      href={href}
    >
      {cta}
    </EditableContent>
  );
}

export function NavigationHeader({
  block,
  locale,
  isActive,
  activeEditTarget,
  editable,
  pages,
  defaultLocale = locale,
  siteMode = 'standard',
  vertical = 'restaurant',
  themeId,
  colorsetId,
  allBlocks,
  hasTastingTimeline,
  supportedLocales,
  publicPathPrefix,
  pathname,
  onNavigateToPageSlug,
  onLocaleChange,
}: BlockRenderProps) {
  const siteTitle = pickBlockField(block.content, 'siteTitle', locale);
  const brandTitle = siteTitle.trim();
  const cta = pickBlockField(block.content, 'ctaLabel', locale);
  const logoImageRaw = pickMediaUrl(block, 'logoImage');
  const target = isActive ? activeEditTarget : null;
  const resolvedDefaultLocale = defaultLocale || locale;
  const localeOptions =
    supportedLocales && supportedLocales.length > 0
      ? supportedLocales
      : [resolvedDefaultLocale];
  const navItems = resolveNavigationTree(block, {
    pages,
    locale,
    defaultLocale: resolvedDefaultLocale,
    siteMode,
    vertical,
    themeId,
    publicPathPrefix,
    siteBlocks: allBlocks,
    hasTastingTimeline,
  });
  const hasLinks = navItems.length > 0;
  const toggleId = `ws-nav-toggle-${block.blockId}`;
  const fromNavRegistry = block.registryBlockKey
    ? NAV_PRESENTATION_FROM_REGISTRY[block.registryBlockKey]
    : undefined;
  // Site-wide Look: use the nav block’s configured presentation (and theme),
  // not the current page’s hero — Menu/About must match Home.
  const navPresentation = resolveNavPresentation(
    themeId,
    readNavPresentationBlockOverride(block.content) ??
      (fromNavRegistry && isNavPresentation(fromNavRegistry) ? fromNavRegistry : null),
    null,
  );
  const logoSurface = resolveNavLogoSurface(themeId, navPresentation, colorsetId);
  const logoImage = logoImageRaw
    ? retargetPlatformNavLogoPathForSurface(logoImageRaw, logoSurface)
    : undefined;
  /** Panel mode twin — paper colorsets swap to on-light; ink-deep keeps on-dark. */
  const logoImagePanel =
    navPresentationUsesCinematicLogo(navPresentation) && logoImageRaw
      ? resolveOverlayAdaptivePanelLogoPath(logoImageRaw, themeId, colorsetId)
      : null;
  const navArrangement = resolveNavArrangementFromRegistry(block.registryBlockKey);
  const drawerHasSocial = pickSiteSocialLinksFromContent(block.content, locale).length > 0;
  /** Live: icons only when profiles exist. Signal edit keeps the empty ghost. */
  const showDrawerSocial =
    drawerHasSocial || (Boolean(editable) && navArrangement === 'signal');
  const showLocaleSwitcher = localeOptions.length > 1;
  const currentPageSlug = resolveNavCurrentPageSlug({
    pathname,
    publicPathPrefix,
    supportedLocales: localeOptions,
  });
  const ctaIntent = readBlockCtaIntent(block.content, locale, {
    hrefFieldKey: 'ctaHref',
    fallback: 'book',
  });
  const ctaHref = resolveBlockCtaHref(ctaIntent, {
    locale,
    defaultLocale: resolvedDefaultLocale,
    content: block.content,
    hrefFieldKey: 'ctaHref',
    siteMode,
    pages,
    allBlocks,
    publicPathPrefix,
  });
  /** Logo / brand name → localized homepage (works from Imprint, Menu, etc.). */
  const brandHref = editable
    ? '#'
    : resolveSectionHref('top', {
        locale,
        defaultLocale: resolvedDefaultLocale,
        publicPathPrefix,
      });

  const localeSwitcherProps = showLocaleSwitcher
    ? {
        locale,
        defaultLocale: resolvedDefaultLocale,
        supportedLocales: localeOptions,
        editable,
        publicPathPrefix,
        pathname,
        onLocaleChange,
      }
    : null;
  const localeSwitcherBar = localeSwitcherProps ? (
    <NavLocaleSwitcher {...localeSwitcherProps} slot="bar" />
  ) : null;
  const localeSwitcherDrawer = localeSwitcherProps ? (
    <NavLocaleSwitcher {...localeSwitcherProps} slot="drawer" />
  ) : null;
  const drawerSocial = showDrawerSocial ? (
    <SiteSocialLinks
      content={block.content}
      locale={locale}
      editable={editable}
      activeEditTarget={target}
      surface="nav"
      showHeading={navArrangement === 'signal' && !localeSwitcherDrawer}
      className="ws-nav__social-wrap--drawer"
    />
  ) : null;
  const drawerSocialBesideLocale = Boolean(localeSwitcherDrawer && drawerSocial);

  return (
    <header
      className={[
        'ws-nav',
        navArrangementClass(navArrangement),
        isActive ? 'ws-block--active' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      data-block-id={block.blockId}
      data-component={block.componentType}
      data-nav-presentation={navPresentation}
      data-nav-arrangement={navArrangement}
      onClick={(event) => {
        if (editable) {
          return;
        }
        const anchor = (event.target as Element | null)?.closest?.('a[href]');
        if (!anchor?.closest('.ws-nav__drawer')) {
          return;
        }
        if (activateNavDrawerHashLink(anchor)) {
          event.preventDefault();
          return;
        }
        const href = anchor.getAttribute('href')?.trim() ?? '';
        if (href && href !== '#') {
          closeNavDrawerFrom(anchor);
        }
      }}
    >
      <div className="ws-nav__inner">
        <a className="ws-nav__brand" href={brandHref}>
          <EditableMedia
            fieldKey="logoImage"
            editable={editable}
            activeEditTarget={target}
            className={['ws-nav__logo', logoImagePanel ? 'ws-nav__logo--hero' : '']
              .filter(Boolean)
              .join(' ')}
            src={logoImage}
            alt={brandTitle}
            width={40}
            height={40}
            loading="eager"
            decoding="async"
          />
          {logoImagePanel ? (
            <EditableMedia
              fieldKey="logoImage"
              editable={editable}
              activeEditTarget={target}
              className="ws-nav__logo ws-nav__logo--panel"
              src={logoImagePanel}
              alt={brandTitle}
              width={40}
              height={40}
              loading="eager"
              decoding="async"
            />
          ) : null}
          {brandTitle ? (
            <EditableContent
              fieldKey="siteTitle"
              editable={editable}
              activeEditTarget={target}
              className="ws-nav__brand-text"
            >
              {brandTitle}
            </EditableContent>
          ) : null}
        </a>

        {hasLinks ? (
          <>
            <div className="ws-nav__bar-actions">
              {localeSwitcherBar}
              <NavCta
                cta={cta}
                editable={editable}
                target={target}
                className="ws-nav__cta--bar"
                href={ctaHref}
              />
              <input
                type="checkbox"
                id={toggleId}
                className="ws-nav__toggle-input"
                aria-hidden="true"
                tabIndex={-1}
              />
              <label htmlFor={toggleId} className="ws-nav__toggle" aria-label={pickChrome('navMenu', locale)}>
                <span className="ws-nav__toggle-icon" aria-hidden="true" />
              </label>
            </div>
            <div className="ws-nav__backdrop" aria-hidden="true" />
            <div className="ws-nav__drawer">
              <div className="ws-nav__drawer-header">
                <a className="ws-nav__drawer-brand" href={brandHref}>
                  {logoImage ? (
                    <img
                      className="ws-nav__drawer-logo"
                      src={logoImage}
                      alt=""
                      width={40}
                      height={40}
                      loading="eager"
                      decoding="async"
                      onError={(event) => {
                        event.currentTarget.removeAttribute('src');
                      }}
                    />
                  ) : null}
                  {brandTitle ? (
                    <span className="ws-nav__drawer-title">{brandTitle}</span>
                  ) : null}
                </a>
                <label
                  htmlFor={toggleId}
                  className="ws-nav__drawer-close"
                  aria-label={pickChrome('navClose', locale)}
                >
                  <span className="ws-nav__drawer-close-icon" aria-hidden="true" />
                </label>
              </div>
              <NavLinks
                blockId={block.blockId}
                items={navItems}
                editable={editable}
                target={target}
                onNavigateToPageSlug={onNavigateToPageSlug}
                currentPageSlug={currentPageSlug}
                locale={locale}
              />
              {drawerSocialBesideLocale ? (
                <div className="ws-nav__drawer-tools">
                  {localeSwitcherDrawer}
                  {drawerSocial}
                </div>
              ) : (
                localeSwitcherDrawer
              )}
              <div className="ws-nav__drawer-end">
                {drawerSocialBesideLocale ? null : drawerSocial}
                <NavCta
                  cta={cta}
                  editable={editable}
                  target={target}
                  className="ws-nav__cta--drawer"
                  href={ctaHref}
                />
              </div>
              <NavCta
                cta={cta}
                editable={editable}
                target={target}
                className="ws-nav__cta--desktop"
                href={ctaHref}
              />
            </div>
          </>
        ) : (
          <>
            {localeSwitcherBar}
            {localeSwitcherDrawer}
            <NavCta
              cta={cta}
              editable={editable}
              target={target}
              className="ws-nav__cta--inline"
              href={ctaHref}
            />
          </>
        )}
      </div>
    </header>
  );
}
